import { createRouter, createWebHistory } from 'vue-router'

import { useAuth } from '@/stores/auth'
import { useAccount } from '@/stores/account'

import { BASE_URL, VITE_APP_HOSTNAME } from '@/config'

const router = createRouter({
  history: createWebHistory(BASE_URL),
  scrollBehavior() {
    return { top: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      meta: {
        title: 'Emailpref — Email Preference Management Tool | Free Plan',
        onlyApp: true,
        allowWww: true,
      },
      component: () => import('@/views/LandingView.vue'),
    },
    {
      path: '/login',
      name: 'login',
      meta: {
        onlyApp: true,
      },
      component: () => import('@/views/LoginView.vue'),
    },
    {
      path: '/signup',
      name: 'signup',
      meta: {
        onlyApp: true,
      },
      component: () => import('@/views/SignupView.vue'),
    },
    {
      path: '/signup-survey',
      name: 'signup-survey',
      meta: {
        title: 'Survey',
        onlyApp: true,
        needsAuth: true,
      },
      component: () => import('@/views/SignupSurvey.vue'),
    },
    {
      path: '/settings',
      component: () => import('@/views/SettingsView.vue'),
      meta: {
        title: 'Settings',
        onlyApp: true,
        needsAuth: true,
      },
      children: [
        { path: '', redirect: '/settings/general' },
        {
          path: 'general',
          name: 'settings-general',
          meta: { title: 'Settings — General', onlyApp: true, needsAuth: true },
          component: () => import('@/views/settings/SettingsGeneral.vue'),
        },
        {
          path: 'workspace',
          name: 'settings-workspace',
          meta: { title: 'Settings — Workspace', onlyApp: true, needsAuth: true },
          component: () => import('@/views/settings/SettingsWorkspace.vue'),
        },
        {
          path: 'email',
          name: 'settings-email',
          meta: { title: 'Settings — Email Config', onlyApp: true, needsAuth: true },
          component: () => import('@/views/settings/SettingsEmail.vue'),
        },
        {
          path: 'billing',
          name: 'settings-billing',
          meta: { title: 'Settings — Billing', onlyApp: true, needsAuth: true },
          component: () => import('@/views/settings/SettingsBilling.vue'),
        },
        {
          path: 'api-keys',
          name: 'settings-api-keys',
          meta: { title: 'Settings — API Keys', onlyApp: true, needsAuth: true },
          component: () => import('@/views/settings/SettingsApiKeys.vue'),
        },
        {
          path: 'danger-zone',
          name: 'settings-danger-zone',
          meta: { title: 'Settings — Danger Zone', onlyApp: true, needsAuth: true },
          component: () => import('@/views/settings/SettingsDangerZone.vue'),
        },
      ],
    },
    {
      path: '/account-settings',
      redirect: '/settings/general',
    },
    {
      path: '/billing',
      redirect: '/settings/billing',
    },
    {
      path: '/user/notification',
      redirect: '/preferences',
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      meta: {
        title: 'Dashboard',
        needsAuth: true,
        onlyApp: true,
      },
      component: () => import('@/views/DashboardView.vue'),
    },
    {
      path: '/preference-pages',
      name: 'preference-pages',
      meta: {
        title: 'Preference Pages',
        needsAuth: true,
        onlyApp: true,
      },
      component: () => import('@/views/PreferencePagesView.vue'),
    },
    {
      path: '/migrations',
      name: 'migrations',
      meta: {
        title: 'Migrations',
        needsAuth: true,
        onlyApp: true,
      },
      component: () => import('@/views/MigrationsView.vue'),
    },
    {
      path: '/migrations/:id',
      name: 'migration-detail',
      meta: {
        title: 'Migration Report',
        needsAuth: true,
        onlyApp: true,
      },
      component: () => import('@/views/MigrationDetailView.vue'),
    },
    {
      path: '/lists',
      name: 'lists',
      meta: {
        title: 'Lists',
        needsAuth: true,
        onlyApp: true,
      },
      component: () => import('@/views/ListsView.vue'),
    },
    {
      path: '/integrations',
      name: 'integrations',
      meta: {
        title: 'Integrations',
        needsAuth: true,
        onlyApp: true,
      },
      component: () => import('@/views/IntegrationsView.vue'),
    },
    {
      path: '/actions',
      name: 'actions',
      meta: {
        title: 'Actions',
        needsAuth: true,
        onlyApp: true,
      },
      component: () => import('@/views/ActionsView.vue'),
    },
    {
      path: '/customers',
      name: 'customers',
      meta: {
        title: 'Customers',
        needsAuth: true,
        onlyApp: true,
        featureFlag: 'customers_tab',
      },
      component: () => import('@/views/CustomersView.vue'),
    },
    {
      path: '/customers/:site/:id',
      name: 'customer-detail',
      meta: {
        title: 'Customer Detail',
        needsAuth: true,
        onlyApp: true,
        featureFlag: 'customers_tab',
      },
      component: () => import('@/views/CustomerDetailView.vue'),
    },
    {
      path: '/analytics',
      name: 'analytics',
      meta: {
        title: 'Analytics',
        needsAuth: true,
        onlyApp: true,
        featureFlag: 'analytics_tab',
      },
      component: () => import('@/views/AnalyticsView.vue'),
    },
    {
      path: '/create-list/:id',
      name: 'createList',
      meta: {
        needsAuth: true,
        onlyApp: true,
      },
      component: () => import('@/components/lists/CreateList.vue'),
      props: true,
    },
    {
      path: '/edit-preference-page/:site/:id',
      name: 'editPreferencePage',
      meta: {
        needsAuth: true,
        onlyApp: true,
      },
      component: () => import('@/views/EditPreferencePageView.vue'),
    },
    {
      path: '/create-preference-page/:site',
      name: 'createPreferencePage',
      meta: {
        needsAuth: true,
        onlyApp: true,
      },
      component: () => import('@/views/EditPreferencePageView.vue'),
    },
    {
      path: '/data-processing-addendum',
      name: 'gdpr',
      meta: {
        title: 'Data Processing Addendum - Emailpref',
        onlyApp: true,
        allowWww: true,
      },
      component: () => import('@/views/GdprView.vue'),
    },
    {
      path: '/terms-of-service',
      name: 'terms-of-service',
      meta: {
        title: 'Terms of Service - Emailpref',
        onlyApp: true,
        allowWww: true,
      },
      component: () => import('@/views/TermsOfServiceView.vue'),
    },
    {
      path: '/privacy-policy',
      name: 'privacy-policy',
      meta: {
        title: 'Privacy Policy - Emailpref',
        onlyApp: true,
        allowWww: true,
      },
      component: () => import('@/views/PrivacyPolicyView.vue'),
    },
    {
      path: '/legal/data-subprocessors',
      name: 'data-subprocessors',
      meta: {
        title: 'Data Subprocessors - Emailpref',
        onlyApp: true,
        allowWww: true,
      },
      component: () => import('@/views/DataSubprocessorsView.vue'),
    },
    {
      path: '/blog-posts/:slug',
      redirect: (to) => ({ path: `/blog/${to.params.slug}` }),
    },
    {
      path: '/blog/resubscribe-from-dashboard',
      redirect: { path: '/blog/resubscribe-unsubscribed-users-with-preference-pages' },
    },
    {
      path: '/blog',
      name: 'blog',
      meta: {
        title: 'Blog - Emailpref',
        onlyApp: true,
        allowWww: true,
      },
      component: () => import('@/views/BlogView.vue'),
    },
    {
      path: '/blog/:slug',
      name: 'blogPost',
      meta: {
        title: 'Blog - Emailpref',
        onlyApp: true,
        allowWww: true,
      },
      component: () => import('@/views/BlogPostView.vue'),
    },
    {
      path: '/supported-integrations',
      name: 'supported-integrations',
      meta: {
        title: 'Supported Integrations - Emailpref',
        onlyApp: true,
        allowWww: true,
      },
      component: () => import('@/views/SupportedIntegrationsView.vue'),
    },
    {
      path: '/integrations/:slug',
      name: 'integration-landing',
      meta: {
        title: 'Integration - Emailpref',
        onlyApp: true,
        allowWww: true,
      },
      component: () => import('@/views/IntegrationLandingView.vue'),
    },
    {
      path: '/docs/:pathMatch(.*)*',
      name: 'docs',
      meta: {
        title: 'Documentation - Emailpref',
        onlyApp: true,
        allowWww: true,
      },
      component: () => import('@/views/docs/DocsView.vue'),
    },
    {
      path: '/404',
      name: 'notFound',
      component: () => import('@/views/NotFoundView.vue'),
    },
    {
      path: '/:customPath(.*)',
      name: 'preferences',
      meta: {
        title: 'Preferences',
      },
      component: () => import('@/views/PreferencesView.vue'),
    },
  ],
})

// Navigation middleware
router.beforeEach(async (to) => {
  document.title = to.meta?.title ?? 'Emailpref'
  const auth = await useAuth()

  if (to.name == 'login' && auth.isLogged) {
    return { name: 'dashboard' }
  }

  if (to.meta.needsAuth) {
    if (!auth.isLogged && to.name !== 'login') {
      return { name: 'login' }
    } else {
      if (auth.organization === 'None' && to.name !== 'signup-survey') {
        return { name: 'signup-survey' }
      } else if (auth.organization !== 'None' && to.name == 'signup-survey') {
        return { name: 'dashboard' }
      }
    }
  }

  // Feature flag gating
  if (to.meta.featureFlag) {
    const account = useAccount()
    // On refresh, account data may not be loaded yet — fetch it before checking flags
    if (auth.isLogged && auth.id && !account.id) {
      try {
        await account.get(auth.id)
      } catch {
        auth.logout()
        return { name: 'login' }
      }
    }
    if (!account.featureFlags?.includes(to.meta.featureFlag)) {
      return { name: 'dashboard' }
    }
  }

  // Check app domain
  if (to.meta.onlyApp) {
    const currentHost = window.location.hostname
    if (currentHost === VITE_APP_HOSTNAME) return
    window.location.href = window.location.toString().replace(currentHost, VITE_APP_HOSTNAME)
    return false
  }

  // Other way, keep navigating
  return
})

export default router
